import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components/ui';
import { hasPermission, hasAnyPermission, isTeamUserAuthenticated } from '@/utils/permissions';
import { toast } from 'react-hot-toast';

interface TeamProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requiredPermissions?: string[];
  requireAllPermissions?: boolean;
  fallback?: React.ReactNode;
  unauthorizedMessage?: string;
}

// Loading component for auth check
const AuthLoading: React.FC = () => (
  <LoadingSpinner
    title="Checking Authentication..."
    subtitle="Please wait while we verify your access"
    size="medium"
  />
);

// Unauthorized component
const UnauthorizedAccess: React.FC<{ message?: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
    <div className="text-red-500 text-6xl mb-4">🚫</div>
    <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
    <p className="text-gray-600 text-center">
      {message || 'You are not authorized to access this page'}
    </p>
  </div>
);

export const TeamProtectedRoute: React.FC<TeamProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requiredPermissions = [],
  requireAllPermissions = false,
  fallback,
  unauthorizedMessage,
}) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  // Show loading while auth is being checked
  if (loading) {
    return <AuthLoading />;
  }

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    // Check if this is a team user trying to access team pages
    const isTeamAuth = isTeamUserAuthenticated();
    if (!isTeamAuth) {
      return <Navigate to="/auth/login" replace state={{ from: location }} />;
    }
  }

  // Check if user is a team user
  if (user?.isTeamUser) {
    // Check permission requirements for team users
    if (requiredPermissions.length > 0) {
      const hasRequiredPermission = requireAllPermissions
        ? requiredPermissions.every(perm => hasPermission(user, perm))
        : hasAnyPermission(user, requiredPermissions);

      if (!hasRequiredPermission) {
        // Show toast notification
        toast.error(unauthorizedMessage || 'You are not authorized to access this page');
        
        // Show fallback or unauthorized component
        if (fallback) {
          return <>{fallback}</>;
        }
        
        return <UnauthorizedAccess message={unauthorizedMessage} />;
      }
    }
  }

  return <>{children}</>;
};

export default TeamProtectedRoute;
